"use client";

import { useEffect, useRef } from "react";
import { sendDiscoveryEventBeacon } from "@/lib/discoveryEventBeacon";
import type { DiscoveryEventKey } from "@/app/actions/trackDiscoveryEventUtils";
export const DEFAULT_SCROLL_DEPTH_THRESHOLDS = [0.25, 0.5, 0.75, 1] as const;

/**
 * Fires `event` once for each threshold in `thresholds` (0-1) once the
 * visitor has scrolled past that fraction of the page's height — or, when
 * `targetSelector` matches an element, that fraction of the way through that
 * element instead, for tracking engagement with one section rather than
 * whole-page scroll depth. Falls back to the page-height behavior if
 * `targetSelector` doesn't match anything, since the target section can be
 * CMS-optional.
 *
 * Checks the viewport's bottom edge against each threshold's absolute
 * position on every scroll tick, rather than watching for an
 * IntersectionObserver visibility *crossing*. IntersectionObserver only
 * checks at browser-frame granularity — a fast scroll (one trackpad flick,
 * or jumping straight to the bottom) can move the viewport past a narrow
 * sentinel entirely between two checks, silently skipping that crossing.
 * A direct position comparison can't miss a threshold: whatever the current
 * scroll position is, every threshold at or above it fires immediately.
 */
export function useScrollDepthEvent(event: DiscoveryEventKey, properties?: Record<string, unknown>, thresholds: readonly number[] = DEFAULT_SCROLL_DEPTH_THRESHOLDS, targetSelector?: string) {
  const firedThresholdsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    firedThresholdsRef.current = new Set();
    const remaining = new Set(thresholds);
    let thresholdPositions: {
      depthThreshold: number;
      y: number;
    }[] = [];
    const computePositions = () => {
      const target = targetSelector ? document.querySelector(targetSelector) : null;
      // A target that hasn't laid out yet (e.g. still hydrating, or behind
      // a lazy/suspended boundary) reports a zero-height rect, which would
      // collapse every threshold onto the same point. Fall back to the
      // page-height behavior for this call; the ResizeObserver below
      // re-runs this once the target has a real height.
      const rect = target && target.getBoundingClientRect().height > 0 ? target.getBoundingClientRect() : null;
      thresholdPositions = thresholds.map(depthThreshold => {
        if (rect) {
          const absoluteTop = rect.top + window.scrollY;
          return {
            depthThreshold,
            y: absoluteTop + rect.height * depthThreshold
          };
        }
        return {
          depthThreshold,
          y: document.documentElement.scrollHeight * depthThreshold
        };
      });
    };
    computePositions();

    // Requires a real scroll before counting a threshold as reached — a
    // page shorter than 2x the viewport has every threshold in view on mount.
    let hasScrolled = false;
    const checkThresholds = () => {
      if (!hasScrolled || remaining.size === 0) {
        return;
      }
      const viewportBottom = window.scrollY + window.innerHeight;
      for (const {
        depthThreshold,
        y
      } of thresholdPositions) {
        if (remaining.has(depthThreshold) && viewportBottom >= y) {
          remaining.delete(depthThreshold);
          firedThresholdsRef.current.add(depthThreshold);
          sendDiscoveryEventBeacon(event, {
            ...properties,
            scroll_depth: depthThreshold
          });
        }
      }
      if (remaining.size === 0) {
        cleanup();
      }
    };
    const onScroll = () => {
      hasScrolled = true;
      checkThresholds();
    };
    window.addEventListener("scroll", onScroll, {
      passive: true
    });

    // Re-measures as content loads in (images, async sections) and shifts
    // the page's total scrollable height, then re-checks in case that
    // shift means an already-passed scroll position now clears a threshold.
    const resizeObserver = new ResizeObserver(() => {
      computePositions();
      checkThresholds();
    });
    resizeObserver.observe(document.documentElement);
    function cleanup() {
      window.removeEventListener("scroll", onScroll);
      resizeObserver.disconnect();
    }
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally set up once per mount
  }, []);
}